import { createSlice } from '@reduxjs/toolkit';
import { apiCallBegan } from './middleware/apiMiddleware';

/**
 * EVENT SLICE - Event Management State
 * Pattern: Follows middleware-based workflow with centralized API calls
 *
 * Each operation (createEvent, updateEvent, deleteEvent) has separate loading/error states
 * and uses middleware for centralized API handling via apiCallBegan action
 */

const initialState = {
    // Events list
    events: [],

    // Fetch All Events operation states
    fetchAllEventsStatus: 'idle', // 'idle' | 'loading' | 'succeeded' | 'failed'
    fetchAllEventsError: null,

    // Featured events (homepage hero + featured section)
    featuredEvents: [],
    fetchFeaturedEventsStatus: 'idle',
    fetchFeaturedEventsError: null,

    // Create Event operation states
    createEventLoading: false,
    createEventError: null,
    createEventSuccess: false,
    createdEvent: null,

    // Publish Event operation states
    publishEventLoading: false,
    publishEventError: null,
    publishEventSuccess: false,
    publishedEvent: null,

    // Update Event operation states
    updateEventLoading: false,
    updateEventError: null,
    updateEventSuccess: false,

    // Delete Event operation states
    deleteEventLoading: false,
    deleteEventError: null,

    // Event Categories
    eventCategories: [],
    fetchCategoriesLoading: false,
    fetchCategoriesError: null,

    // Event Subcategories
    eventSubCategories: [],
    fetchSubCategoriesLoading: false,
    fetchSubCategoriesError: null,

    // Currencies
    currencies: [],
    fetchCurrenciesLoading: false,
    fetchCurrenciesError: null,

    // Ticket Categories
    ticketCategories: [],
    fetchTicketCategoriesLoading: false,
    fetchTicketCategoriesError: null,

    // Event Tables (availability for booking)
    eventTables: [],
    fetchEventTablesLoading: false,
    fetchEventTablesError: null,

    // Current Event (fetch by ID for edit mode)
    currentEvent: null,
    fetchEventByIdLoading: false,
    fetchEventByIdError: null,
    fetchEventByIdStatus: 'idle', // 'idle' | 'loading' | 'succeeded' | 'failed'

    // Public event by slug (detail / booking / payment pages)
    publicEventBySlug: null,
    fetchEventBySlugStatus: 'idle',
    fetchEventBySlugError: null,

    // General event state
    selectedEventType: null,

    // Category Events (filtered by category)
    categoryEvents: [],
    fetchCategoryEventsStatus: 'idle',
    fetchCategoryEventsError: null,
    currentCategoryBySlug: null,
    fetchCategoryBySlugStatus: 'idle',
    fetchCategoryBySlugError: null,

    // My Events (LoggedIn User's events)
    myEvents: [],
    fetchMyEventsLoading: false,
    fetchMyEventsError: null,

    // All Stats
    allStats: [],
    fetchAllStatsLoading: false,
    fetchAllStatsError: null,

    // Submit Enquiry
    submitEnquiryLoading: false,
    submitEnquiryError: null,
    submitEnquirySuccess: false,
};

const eventSlice = createSlice({
    name: 'event',
    initialState,
    reducers: {
        // ============ FETCH ALL EVENTS REDUCERS ============
        beginFetchAllEventsLoader: (state) => {
            state.fetchAllEventsStatus = "loading";
            state.fetchAllEventsError = null;
        },
        setFetchAllEventsData: (state, action) => {
            state.fetchAllEventsStatus = "completed";
            state.events = action.payload;
            state.fetchAllEventsError = null;
        },
        fetchAllEventsError: (state, action) => {
            state.fetchAllEventsStatus = "failed";
            state.fetchAllEventsError = action.payload?.errorMessage || 'Failed to fetch events';
        },
        beginFetchFeaturedEventsLoader: (state) => {
            state.fetchFeaturedEventsStatus = "loading";
            state.fetchFeaturedEventsError = null;
        },
        setFetchFeaturedEventsData: (state, action) => {
            state.fetchFeaturedEventsStatus = "completed";
            const raw = action.payload?.data;
            state.featuredEvents = Array.isArray(raw) ? raw : [];
            state.fetchFeaturedEventsError = null;
        },
        fetchFeaturedEventsError: (state, action) => {
            state.fetchFeaturedEventsStatus = "failed";
            state.fetchFeaturedEventsError =
                action.payload?.errorMessage || "Failed to fetch featured events";
        },
        // ============ FETCH ALL STATS REDUCERS ============
        beginFetchAllStatsLoader: (state) => {
            state.fetchAllStatsLoading = true;
            state.fetchAllStatsError = null;
        },
        setFetchAllStatsData: (state, action) => {
            state.fetchAllStatsLoading = false;
            state.allStats = action.payload;
            state.fetchAllStatsError = null;
        },
        fetchAllStatsError: (state, action) => {
            state.fetchAllStatsLoading = false;
            state.fetchAllStatsError = action.payload?.errorMessage || 'Failed to fetch all stats';
        },
        // ============ SUBMIT ENQUIRY REDUCERS ============
        beginSubmitEnquiryLoader: (state) => {
            state.submitEnquiryLoading = true;
            state.submitEnquiryError = null;
        },
        setSubmitEnquiryData: (state) => {
            state.submitEnquiryLoading = false;
            state.submitEnquirySuccess = true;
            state.submitEnquiryError = null;
        },
        setSubmitEnquiryError: (state, action) => {
            state.submitEnquiryLoading = false;
            state.submitEnquiryError = action.payload?.errorMessage || 'Failed to submit enquiry';
        },
        resetSubmitEnquiryStatus: (state) => {
            state.submitEnquiryLoading = false;
            state.submitEnquiryError = null;
            state.submitEnquirySuccess = false;
        },
        // ============ CREATE EVENT REDUCERS ============
        beginCreateEventLoader: (state) => {
            state.createEventLoading = true;
            state.createEventError = null;
        },
        setCreateEventData: (state, action) => {
            state.createEventLoading = false;
            // Safely push to events array - ensure events is an array first
            if (Array.isArray(state.events)) {
                state.events.push(action.payload);
            } else {
                state.events = [action.payload];
            }
            state.createdEvent = action.payload;
            state.createEventSuccess = true;
            state.createEventError = null;
        },
        createEventError: (state, action) => {
            state.createEventLoading = false;
            state.createEventError = action.payload?.errorMessage || 'Failed to create event';
        },

        // ============ PUBLISH EVENT REDUCERS ============
        beginPublishEventLoader: (state) => {
            state.publishEventLoading = true;
            state.publishEventError = null;
            state.publishEventSuccess = false;
        },

        setPublishEventData: (state, action) => {
            state.publishEventLoading = false;
            state.publishEventError = null;
            state.publishEventSuccess = true;
            state.publishedEvent = action.payload;
        },

        publishEventError: (state, action) => {
            state.publishEventLoading = false;
            state.publishEventSuccess = false;
            state.publishEventError =
                action.payload?.errorMessage || 'Failed to publish event';
        },

        resetPublishEventSuccess: (state) => {
            state.publishEventSuccess = false;
            state.publishedEvent = null;
        },

        clearPublishEventError: (state) => {
            state.publishEventError = null;
        },

        // ============ UPDATE EVENT REDUCERS ============
        beginUpdateEventLoader: (state) => {
            state.updateEventLoading = true;
            state.updateEventSuccess = false;
            state.updateEventError = null;
        },
        setUpdateEventData: (state, action) => {
            state.updateEventLoading = false;

            // Handle both array and {data: [...]} structures for state.events
            const eventsArray = Array.isArray(state.events)
                ? state.events
                : (state.events?.data || []);

            if (Array.isArray(eventsArray)) {
                const index = eventsArray.findIndex(e => e.id === action.payload.id);
                if (index !== -1) {
                    // Update in the correct location based on structure
                    if (Array.isArray(state.events)) {
                        state.events[index] = action.payload;
                    } else if (state.events?.data) {
                        state.events.data[index] = action.payload;
                    }
                }
            }

            state.updateEventSuccess = true;
            state.updateEventError = null;
            console.log('✅ Event updated successfully:', action.payload.id);
        },
        updateEventError: (state, action) => {
            state.updateEventLoading = false;
            state.updateEventError = action.payload?.errorMessage || 'Failed to update event';
        },

        // ============ DELETE EVENT REDUCERS ============
        beginDeleteEventLoader: (state) => {
            state.deleteEventLoading = true;
            state.deleteEventError = null;
        },
        setDeleteEventData: (state, action) => {
            state.deleteEventLoading = false;
            // Get eventId from meta (passed via thunk) or from response
            const deletedId = action.payload?.eventId || action.payload?.id;

            // Handle both array and {data: [...]} structures for state.events
            if (Array.isArray(state.events)) {
                state.events = state.events.filter(e => e.id !== deletedId);
            } else if (state.events?.data && Array.isArray(state.events.data)) {
                state.events.data = state.events.data.filter(e => e.id !== deletedId);
            }
            state.deleteEventError = null;
        },
        deleteEventError: (state, action) => {
            state.deleteEventLoading = false;
            state.deleteEventError = action.payload?.errorMessage || 'Failed to delete event';
        },

        // ============ FETCH CATEGORY EVENTS REDUCERS ============
        beginFetchCategoryEventsLoader: (state) => {
            state.fetchCategoryEventsStatus = 'loading';
            state.fetchCategoryEventsError = null;
        },
        setFetchCategoryEventsData: (state, action) => {
            state.fetchCategoryEventsStatus = 'completed';
            const raw = action.payload?.data;
            state.categoryEvents = Array.isArray(raw) ? raw : [];
            state.fetchCategoryEventsError = null;
        },
        fetchCategoryEventsError: (state, action) => {
            state.fetchCategoryEventsStatus = 'failed';
            state.categoryEvents = [];
            state.fetchCategoryEventsError = action.payload?.errorMessage || 'Failed to fetch category events';
        },
        clearCategoryEvents: (state) => {
            state.categoryEvents = [];
            state.fetchCategoryEventsStatus = 'idle';
            state.fetchCategoryEventsError = null;
            state.currentCategoryBySlug = null;
            state.fetchCategoryBySlugStatus = 'idle';
            state.fetchCategoryBySlugError = null;
        },
        beginFetchCategoryBySlugLoader: (state) => {
            state.fetchCategoryBySlugStatus = 'loading';
            state.fetchCategoryBySlugError = null;
        },
        setFetchCategoryBySlugData: (state, action) => {
            state.fetchCategoryBySlugStatus = 'completed';
            state.currentCategoryBySlug = action.payload?.data || action.payload;
            state.fetchCategoryBySlugError = null;
        },
        fetchCategoryBySlugError: (state, action) => {
            state.fetchCategoryBySlugStatus = 'failed';
            state.currentCategoryBySlug = null;
            state.fetchCategoryBySlugError =
                action.payload?.errorMessage || 'Failed to fetch category';
        },

        // ============ FETCH MY EVENTS REDUCERS ============
        beginFetchMyEventsLoader: (state) => {
            state.fetchMyEventsLoading = true;
            state.fetchMyEventsError = null;
        },
        setFetchMyEventsData: (state, action) => {
            state.fetchMyEventsLoading = false;
            state.myEvents = action.payload;
            state.fetchMyEventsError = null;
        },
        fetchMyEventsError: (state, action) => {
            state.fetchMyEventsLoading = false;
            state.fetchMyEventsError = action.payload?.errorMessage || 'Failed to fetch my events';
        },

        // ============ GENERAL REDUCERS ============
        setEventType: (state, action) => {
            state.selectedEventType = action.payload;
        },
        clearCreateEventError: (state) => {
            state.createEventError = null;
        },
        clearUpdateEventError: (state) => {
            state.updateEventError = null;
        },
        clearDeleteEventError: (state) => {
            state.deleteEventError = null;
        },
        resetCreateEventSuccess: (state) => {
            state.createEventSuccess = false;
        },

        // Reset update success state (for cleanup after redirect)
        resetUpdateEventSuccess: (state) => {
            state.updateEventSuccess = false;
        },

        // ============ EVENT CATEGORIES REDUCERS ============
        beginFetchCategoriesLoader: (state) => {
            state.fetchCategoriesLoading = true;
            state.fetchCategoriesError = null;
        },
        setFetchCategoriesData: (state, action) => {
            state.fetchCategoriesLoading = false;
            // Handle { data: [...] } structure or direct array
            const data = action.payload?.data || action.payload;
            state.eventCategories = Array.isArray(data) ? data : [];
            state.fetchCategoriesError = null;
        },
        fetchCategoriesError: (state, action) => {
            state.fetchCategoriesLoading = false;
            state.fetchCategoriesError = action.payload?.errorMessage || 'Failed to fetch categories';
        },

        // ============ EVENT SUBCATEGORIES REDUCERS ============
        beginFetchSubCategoriesLoader: (state) => {
            state.fetchSubCategoriesLoading = true;
            state.fetchSubCategoriesError = null;
        },
        setFetchSubCategoriesData: (state, action) => {
            state.fetchSubCategoriesLoading = false;
            // Handle { data: [...] } structure or direct array
            const data = action.payload?.data || action.payload;
            state.eventSubCategories = Array.isArray(data) ? data : [];
            state.fetchSubCategoriesError = null;
        },
        fetchSubCategoriesError: (state, action) => {
            state.fetchSubCategoriesLoading = false;
            state.fetchSubCategoriesError = action.payload?.errorMessage || 'Failed to fetch subcategories';
        },
        clearSubCategories: (state) => {
            state.eventSubCategories = [];
        },

        // ============ CURRENCIES REDUCERS ============
        beginFetchCurrenciesLoader: (state) => {
            state.fetchCurrenciesLoading = true;
            state.fetchCurrenciesError = null;
        },
        setFetchCurrenciesData: (state, action) => {
            state.fetchCurrenciesLoading = false;
            // Handle { data: [...] } structure or direct array
            const data = action.payload?.data || action.payload;
            state.currencies = Array.isArray(data) ? data : [];
            state.fetchCurrenciesError = null;
        },
        fetchCurrenciesError: (state, action) => {
            state.fetchCurrenciesLoading = false;
            state.fetchCurrenciesError = action.payload?.errorMessage || 'Failed to fetch currencies';
        },

        // ============ TICKET CATEGORIES REDUCERS ============
        beginFetchTicketCategoriesLoader: (state) => {
            state.fetchTicketCategoriesLoading = true;
            state.fetchTicketCategoriesError = null;
        },
        setFetchTicketCategoriesData: (state, action) => {
            state.fetchTicketCategoriesLoading = false;
            // Handle { data: [...] } structure or direct array
            const data = action.payload?.data || action.payload;
            state.ticketCategories = Array.isArray(data) ? data : [];
            state.fetchTicketCategoriesError = null;
        },
        fetchTicketCategoriesError: (state, action) => {
            state.fetchTicketCategoriesLoading = false;
            state.fetchTicketCategoriesError = action.payload?.errorMessage || 'Failed to fetch ticket categories';
        },

        // ============ FETCH EVENT BY ID REDUCERS ============
        beginFetchEventByIdLoader: (state) => {
            state.fetchEventByIdLoading = true;
            state.fetchEventByIdStatus = 'loading';
            state.fetchEventByIdError = null;
        },
        setFetchEventByIdData: (state, action) => {
            state.fetchEventByIdLoading = false;
            state.fetchEventByIdStatus = 'succeeded';
            state.currentEvent = action.payload;
            state.fetchEventByIdError = null;
        },
        fetchEventByIdError: (state, action) => {
            state.fetchEventByIdLoading = false;
            state.fetchEventByIdStatus = 'failed';
            state.fetchEventByIdError = action.payload?.errorMessage || 'Failed to fetch event details';
        },
        clearCurrentEvent: (state) => {
            state.currentEvent = null;
            state.fetchEventByIdStatus = 'idle';
            state.fetchEventByIdError = null;
            state.fetchEventByIdLoading = false;
        },

        // ============ FETCH EVENT BY SLUG REDUCERS ============
        beginFetchEventBySlugLoader: (state) => {
            state.fetchEventBySlugStatus = 'loading';
            state.fetchEventBySlugError = null;
        },
        setFetchEventBySlugData: (state, action) => {
            state.fetchEventBySlugStatus = 'completed';
            state.publicEventBySlug = action.payload?.data || action.payload;
            state.fetchEventBySlugError = null;
        },
        fetchEventBySlugError: (state, action) => {
            state.fetchEventBySlugStatus = 'failed';
            state.publicEventBySlug = null;
            state.fetchEventBySlugError =
                action.payload?.errorMessage || 'Failed to fetch event details';
        },
        clearPublicEventBySlug: (state) => {
            state.publicEventBySlug = null;
            state.fetchEventBySlugStatus = 'idle';
            state.fetchEventBySlugError = null;
        },

        // ============ FETCH EVENT TABLES REDUCERS ============
        beginFetchEventTablesLoader: (state) => {
            state.fetchEventTablesLoading = true;
            state.fetchEventTablesError = null;
        },
        setFetchEventTablesData: (state, action) => {
            state.fetchEventTablesLoading = false;
            state.eventTables = action.payload?.data || action.payload || [];
            state.fetchEventTablesError = null;
        },
        fetchEventTablesError: (state, action) => {
            state.fetchEventTablesLoading = false;
            state.eventTables = [];
            state.fetchEventTablesError = action.payload?.errorMessage || 'Failed to fetch event tables';
        },
        clearEventTables: (state) => {
            state.eventTables = [];
            state.fetchEventTablesLoading = false;
            state.fetchEventTablesError = null;
        },
    },
});

// ============ EXPORT ACTIONS ============
export const {
    beginFetchAllEventsLoader,
    setFetchAllEventsData,
    fetchAllEventsError,
    beginFetchFeaturedEventsLoader,
    setFetchFeaturedEventsData,
    fetchFeaturedEventsError,
    beginFetchAllStatsLoader,
    setFetchAllStatsData,
    fetchAllStatsError,
    beginSubmitEnquiryLoader,
    setSubmitEnquiryData,
    setSubmitEnquiryError,
    resetSubmitEnquiryStatus,
    beginCreateEventLoader,
    setCreateEventData,
    createEventError,
    beginPublishEventLoader,
    setPublishEventData,
    publishEventError,
    resetPublishEventSuccess,
    clearPublishEventError,
    beginUpdateEventLoader,
    setUpdateEventData,
    updateEventError,
    beginDeleteEventLoader,
    setDeleteEventData,
    deleteEventError,
    setEventType,
    clearCreateEventError,
    clearUpdateEventError,
    clearDeleteEventError,
    // My Events
    beginFetchMyEventsLoader,
    setFetchMyEventsData,
    fetchMyEventsError,
    resetCreateEventSuccess,
    resetUpdateEventSuccess,
    // Categories
    beginFetchCategoriesLoader,
    setFetchCategoriesData,
    fetchCategoriesError,
    // SubCategories
    beginFetchSubCategoriesLoader,
    setFetchSubCategoriesData,
    fetchSubCategoriesError,
    clearSubCategories,
    // Currencies
    beginFetchCurrenciesLoader,
    setFetchCurrenciesData,
    fetchCurrenciesError,
    // Ticket Categories
    beginFetchTicketCategoriesLoader,
    setFetchTicketCategoriesData,
    fetchTicketCategoriesError,

    // Current Event (fetch by ID)
    beginFetchEventByIdLoader,
    setFetchEventByIdData,
    fetchEventByIdError,
    clearCurrentEvent,
    beginFetchEventBySlugLoader,
    setFetchEventBySlugData,
    fetchEventBySlugError,
    clearPublicEventBySlug,
    beginFetchEventTablesLoader,
    setFetchEventTablesData,
    fetchEventTablesError,
    clearEventTables,
    // Category Events
    beginFetchCategoryEventsLoader,
    setFetchCategoryEventsData,
    fetchCategoryEventsError,
    clearCategoryEvents,
    beginFetchCategoryBySlugLoader,
    setFetchCategoryBySlugData,
    fetchCategoryBySlugError,
} = eventSlice.actions;

// ============ THUNK ACTIONS - Dispatch to middleware ============

/**
 * FETCH MY EVENTS ACTION
 * Dispatches middleware action to handle fetch my events API call
 * Pattern: Action → Thunk → Middleware → API Request → Reducer
 */
export const fetchMyEvents = () => (dispatch) => {
    const url = '/my-events';
    const method = 'GET';

    dispatch(
        apiCallBegan({
            url,
            method,
            onStart: beginFetchMyEventsLoader.type,
            onSuccess: setFetchMyEventsData.type,
            onError: fetchMyEventsError.type,
            showGlobalError: true,
        })
    );
};

/**
 * FETCH CATEGORY BY SLUG
 *
 * @param {string} slug - Category URL slug from the database
 */
export const fetchCategoryBySlug = (slug) => (dispatch) => {
    dispatch(
        apiCallBegan({
            url: `/event-categories/slug/${slug}`,
            method: 'GET',
            onStart: beginFetchCategoryBySlugLoader.type,
            onSuccess: setFetchCategoryBySlugData.type,
            onError: fetchCategoryBySlugError.type,
            showGlobalError: false,
        })
    );
};

/**
 * FETCH EVENTS BY CATEGORY SLUG
 *
 * @param {string} categorySlug - Category URL slug from the database
 */
export const fetchEventsByCategory = (categorySlug) => (dispatch) => {
    dispatch(
        apiCallBegan({
            url: '/events',
            method: 'GET',
            params: { category_slug: categorySlug },
            onStart: beginFetchCategoryEventsLoader.type,
            onSuccess: setFetchCategoryEventsData.type,
            onError: fetchCategoryEventsError.type,
            showGlobalError: false,
        })
    );
};

/**
 * FETCH ALL EVENTS ACTION
 * Dispatches middleware action to handle fetch all events API call
 * Pattern: Action → Thunk → Middleware → API Request → Reducer
 */
export const fetchAllEvents = () => (dispatch) => {
    const url = '/events';
    const method = 'GET';

    dispatch(
        apiCallBegan({
            url,
            method,
            onStart: beginFetchAllEventsLoader.type,
            onSuccess: setFetchAllEventsData.type,
            onError: fetchAllEventsError.type,
            showGlobalError: true,
        })
    );
};

/**
 * Featured events for homepage (hero + featured row). GET /events/featured?limit=
 */
export const fetchFeaturedEvents = (limit = 12) => (dispatch) => {
    dispatch(
        apiCallBegan({
            url: '/events/featured',
            method: 'GET',
            params: { limit },
            onStart: beginFetchFeaturedEventsLoader.type,
            onSuccess: setFetchFeaturedEventsData.type,
            onError: fetchFeaturedEventsError.type,
            showGlobalError: false,
        })
    );
};

/**
 * FETCH EVENT BY SLUG (public detail page)
 *
 * @param {string} slug - Event URL slug from the database
 */
export const fetchEventBySlug = (slug) => (dispatch) => {
    dispatch(
        apiCallBegan({
            url: `/events/slug/${slug}`,
            method: 'GET',
            onStart: beginFetchEventBySlugLoader.type,
            onSuccess: setFetchEventBySlugData.type,
            onError: fetchEventBySlugError.type,
            showGlobalError: false,
        })
    );
};

/**
 * FETCH EVENT TABLES (availability for per_table booking)
 *
 * @param {string|number} eventId - Event ID
 */
export const fetchEventTables = (eventId) => (dispatch) => {
    dispatch(
        apiCallBegan({
            url: `/event-tables/${eventId}`,
            method: 'GET',
            onStart: beginFetchEventTablesLoader.type,
            onSuccess: setFetchEventTablesData.type,
            onError: fetchEventTablesError.type,
            showGlobalError: false,
        })
    );
};

/**
 * FETCH EVENT BY ID ACTION
 * Fetches complete event data including event_media for edit mode
 * The list API doesn't return event_media, so we need to fetch individual event
 *
 * @param {string|number} eventId - Event ID to fetch
 */
export const fetchEventById = (eventId) => (dispatch) => {
    const url = `/event/${eventId}`;
    const method = 'GET';

    console.log('🔍 Fetching individual event:', eventId);

    dispatch(
        apiCallBegan({
            url,
            method,
            onStart: beginFetchEventByIdLoader.type,
            onSuccess: setFetchEventByIdData.type,
            onError: fetchEventByIdError.type,
            showGlobalError: true,
        })
    );
};

/**
 * CREATE EVENT ACTION
 * Dispatches middleware action to handle create event API call
 * Pattern: Action → Thunk → Middleware → API Request → Reducer
 *
 * @param {Object} eventData - Event payload with all event details
 */
export const createEvent = (eventData) => (dispatch) => {
    const url = '/create-event';
    const method = 'POST';

    dispatch(
        apiCallBegan({
            url,
            method,
            data: eventData,
            onStart: beginCreateEventLoader.type,
            onSuccess: setCreateEventData.type,
            onError: createEventError.type,
            showGlobalError: true,
        })
    );
};

/**
 * PUBLISH EVENT ACTION
 * Publishes an already-created event.
 *
 * @param {string|number} eventId - Event ID returned by /create-event
 */
export const publishEvent = (eventId) => (dispatch) => {
    const url = `/publish-event/${eventId}`;
    const method = 'PUT';

    dispatch(
        apiCallBegan({
            url,
            method,
            onStart: beginPublishEventLoader.type,
            onSuccess: setPublishEventData.type,
            onError: publishEventError.type,
            showGlobalError: true,
        })
    );
};

/**
 * UPDATE EVENT ACTION
 * Dispatches middleware action to handle update event API call
 * NOTE: Per Swagger docs, endpoint is PUT /update-event (no ID in URL)
 *       The event_id must be included in the request body
 *
 * @param {Object} eventData - Event payload with updated details
 * @param {string|number} eventId - Event ID to update (will be added to body)
 */
export const updateEvent = (eventData, eventId) => (dispatch) => {
    const url = `/update-event/${eventId}`; // PUT /update-event/{event_id} per OpenAPI spec
    const method = 'PUT';

    // Add ID to payload body (to cover all bases)
    const dataWithId = {
        ...eventData,
        id: parseInt(eventId)
    };

    dispatch(
        apiCallBegan({
            url,
            method,
            data: dataWithId,
            onStart: beginUpdateEventLoader.type,
            onSuccess: setUpdateEventData.type,
            onError: updateEventError.type,
            showGlobalError: true,
        })
    );
};

/**
 * DELETE EVENT ACTION
 * Dispatches middleware action to handle delete event API call
 *
 * @param {string} eventId - Event ID to delete
 * @param {boolean} isSoftDelete - Whether to soft delete (default: true)
 */
export const deleteEvent = (eventId, isSoftDelete = true) => (dispatch) => {
    const url = `/delete-event/${isSoftDelete}/${eventId}`;
    const method = 'DELETE';

    dispatch(
        apiCallBegan({
            url,
            method,
            onStart: beginDeleteEventLoader.type,
            onSuccess: setDeleteEventData.type,
            onError: deleteEventError.type,
            showGlobalError: true,
            meta: { eventId },
        })
    );
};

/**
 * FETCH EVENT CATEGORIES ACTION
 * Dispatches middleware action to fetch all event categories
 */
export const fetchEventCategories = () => (dispatch) => {
    const url = '/event-categories';
    const method = 'GET';

    dispatch(
        apiCallBegan({
            url,
            method,
            onStart: beginFetchCategoriesLoader.type,
            onSuccess: setFetchCategoriesData.type,
            onError: fetchCategoriesError.type,
            showGlobalError: false,
        })
    );
};

/**
 * FETCH EVENT SUBCATEGORIES ACTION
 * Dispatches middleware action to fetch subcategories by category ID
 * 
 * @param {number} categoryId - Category ID to fetch subcategories for
 */
export const fetchEventSubCategories = (categoryId) => (dispatch) => {
    const url = `/event-sub-category/${categoryId}`;
    const method = 'GET';

    dispatch(
        apiCallBegan({
            url,
            method,
            onStart: beginFetchSubCategoriesLoader.type,
            onSuccess: setFetchSubCategoriesData.type,
            onError: fetchSubCategoriesError.type,
            showGlobalError: false,
        })
    );
};

/**
 * FETCH CURRENCIES ACTION
 * Dispatches middleware action to fetch all currencies
 */
export const fetchCurrencies = () => (dispatch) => {
    const url = '/currencies';
    const method = 'GET';

    dispatch(
        apiCallBegan({
            url,
            method,
            onStart: beginFetchCurrenciesLoader.type,
            onSuccess: setFetchCurrenciesData.type,
            onError: fetchCurrenciesError.type,
            showGlobalError: false,
        })
    );
};

/**
 * FETCH TICKET CATEGORIES ACTION
 * Dispatches middleware action to fetch all ticket categories
 */
export const fetchTicketCategories = () => (dispatch) => {
    const url = '/ticket-category';
    const method = 'GET';

    dispatch(
        apiCallBegan({
            url,
            method,
            onStart: beginFetchTicketCategoriesLoader.type,
            onSuccess: setFetchTicketCategoriesData.type,
            onError: fetchTicketCategoriesError.type,
            showGlobalError: false,
        })
    );
};

/**
 * SUBMIT ENQUIRY ACTION
 * Dispatches middleware action to handle submit enquiry API call
 */
export const submitEnquiry = (enquiryData) => (dispatch) => {
    const url = '/submit-enquiry';
    const method = 'POST';

    dispatch(
        apiCallBegan({
            url,
            method,
            data: enquiryData,
            onStart: beginSubmitEnquiryLoader.type,
            onSuccess: setSubmitEnquiryData.type,
            onError: setSubmitEnquiryError.type,
            showGlobalError: false,
        })
    );
};

/**
 * FETCH ALL STATS ACTION
 * Dispatches middleware action to handle fetch all stats API call
 */
export const fetchAllStats = () => (dispatch) => {
    const url = '/dashboard-counts';
    const method = 'GET';
    dispatch(
        apiCallBegan({
            url,
            method,
            onStart: beginFetchAllStatsLoader.type,
            onSuccess: setFetchAllStatsData.type,
            onError: fetchAllStatsError.type,
            showGlobalError: false,
        })
    );
};


// ============ SELECTORS ============

/**
 * Selector hooks for consuming fetch all events state
 */
export const selectFetchAllEventsStatus = (state) => state.event.fetchAllEventsStatus;
export const selectFetchAllEventsError = (state) => state.event.fetchAllEventsError;
export const selectFeaturedEvents = (state) => state.event.featuredEvents;
export const selectFetchFeaturedEventsStatus = (state) => state.event.fetchFeaturedEventsStatus;
export const selectFetchFeaturedEventsError = (state) => state.event.fetchFeaturedEventsError;

/**
 * Selector hooks for consuming create event state
 */
export const selectCreateEventLoading = (state) => state.event.createEventLoading;
export const selectCreateEventError = (state) => state.event.createEventError;
export const selectCreateEventSuccess = (state) => state.event.createEventSuccess;
export const selectCreatedEvent = (state) => state.event.createdEvent;
export const selectEvents = (state) => state.event.events;

/**
 * Selector hooks for consuming publish event state
 */
export const selectPublishEventLoading = (state) => state.event.publishEventLoading;
export const selectPublishEventError = (state) => state.event.publishEventError;
export const selectPublishEventSuccess = (state) => state.event.publishEventSuccess;
export const selectPublishedEvent = (state) => state.event.publishedEvent;

/**
 * Selector hooks for consuming update event state
 */
export const selectUpdateEventLoading = (state) => state.event.updateEventLoading;
export const selectUpdateEventError = (state) => state.event.updateEventError;
export const selectUpdateEventSuccess = (state) => state.event.updateEventSuccess;

/**
 * Selector hooks for consuming delete event state
 */
export const selectDeleteEventLoading = (state) => state.event.deleteEventLoading;
export const selectDeleteEventError = (state) => state.event.deleteEventError;

/**
 * Selector hooks for consuming event categories state
 */
export const selectEventCategories = (state) => state.event.eventCategories;
export const selectFetchCategoriesLoading = (state) => state.event.fetchCategoriesLoading;
export const selectFetchCategoriesError = (state) => state.event.fetchCategoriesError;

/**
 * Selector hooks for consuming event subcategories state
 */
export const selectEventSubCategories = (state) => state.event.eventSubCategories;
export const selectFetchSubCategoriesLoading = (state) => state.event.fetchSubCategoriesLoading;
export const selectFetchSubCategoriesError = (state) => state.event.fetchSubCategoriesError;

/**
 * Selector hooks for consuming currencies state
 */
export const selectCurrencies = (state) => state.event.currencies;
export const selectFetchCurrenciesLoading = (state) => state.event.fetchCurrenciesLoading;
export const selectFetchCurrenciesError = (state) => state.event.fetchCurrenciesError;

/**
 * Selector hooks for consuming ticket categories state
 */
export const selectTicketCategories = (state) => state.event.ticketCategories;
export const selectFetchTicketCategoriesLoading = (state) => state.event.fetchTicketCategoriesLoading;
export const selectFetchTicketCategoriesError = (state) => state.event.fetchTicketCategoriesError;

export const selectEventTables = (state) => state.event.eventTables;
export const selectFetchEventTablesLoading = (state) => state.event.fetchEventTablesLoading;
export const selectFetchEventTablesError = (state) => state.event.fetchEventTablesError;

/**
 * Selector hooks for current event (fetch by ID for edit mode)
 */
export const selectCurrentEvent = (state) => state.event.currentEvent;
export const selectFetchEventByIdLoading = (state) => state.event.fetchEventByIdLoading;
export const selectFetchEventByIdError = (state) => state.event.fetchEventByIdError;
export const selectFetchEventByIdStatus = (state) => state.event.fetchEventByIdStatus;

export const selectPublicEventBySlug = (state) => state.event.publicEventBySlug;
export const selectFetchEventBySlugStatus = (state) => state.event.fetchEventBySlugStatus;
export const selectFetchEventBySlugError = (state) => state.event.fetchEventBySlugError;

/**
 * Selector hooks for consuming my events state
 */
export const selectMyEvents = (state) => state.event.myEvents;
export const selectFetchMyEventsLoading = (state) => state.event.fetchMyEventsLoading;
export const selectFetchMyEventsError = (state) => state.event.fetchMyEventsError;

/**
 * Selector hooks for consuming category events state
 */
export const selectCategoryEvents = (state) => state.event.categoryEvents;
export const selectFetchCategoryEventsStatus = (state) => state.event.fetchCategoryEventsStatus;
export const selectFetchCategoryEventsError = (state) => state.event.fetchCategoryEventsError;
export const selectCurrentCategoryBySlug = (state) => state.event.currentCategoryBySlug;
export const selectFetchCategoryBySlugStatus = (state) => state.event.fetchCategoryBySlugStatus;
export const selectFetchCategoryBySlugError = (state) => state.event.fetchCategoryBySlugError;

export default eventSlice.reducer;
